// Colour maths.
//
// Enough of it to derive a second step from a colour someone picked, and to say
// whether that colour can actually be seen. Both are needed because the
// Customization drawer lets people choose, and a picker with no contrast
// readout is a picker that produces unreadable charts.
//
// Pure functions in their own module so test/ui/colour.test.js can check the
// WCAG arithmetic against known values without mounting anything.

import { t } from './i18n.js'

export function hex(c) {
  const v = String(c).trim().replace('#', '')
  const full = v.length === 3 ? [...v].map((x) => x + x).join('') : v
  const n = Number.parseInt(full, 16)
  return Number.isFinite(n) && full.length === 6 ? [(n >> 16) & 255, (n >> 8) & 255, n & 255] : null
}

export const toHex = (rgb) =>
  `#${rgb
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`

/** `amount` of `b` mixed into `a`. Used to derive the soft step from the strong one. */
export function mix(a, b, amount) {
  const [x, y] = [hex(a), hex(b)]
  if (!x || !y) return a
  return toHex(x.map((v, i) => v + (y[i] - v) * amount))
}

/** WCAG relative luminance. */
export function luminance(c) {
  const rgb = hex(c)
  if (!rgb) return 0
  const [r, g, b] = rgb.map((v) => {
    const x = v / 255
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a, b) {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m)
  return (x + 0.05) / (y + 0.05)
}

/**
 * Black or white on a filled background, whichever can be read.
 *
 * Everything the page fills with the accent — the selected date, a segmented
 * button, the primary action — takes this rather than a hardcoded white, which
 * is 2.46:1 on the dark theme's pale blue and worse on anything paler.
 */
export const onAccent = (colour) =>
  contrast(colour, '#ffffff') >= contrast(colour, '#0b0b0b') ? '#ffffff' : '#0b0b0b'

/**
 * What a colour has to clear, and against what.
 *
 * A chart mark is a graphical object: WCAG 2.2 §1.4.11 asks 3:1 against its
 * background. The accent is also used as text and as a fill behind text, so it
 * is held to 4.5:1 on the harder of the two.
 */
export const MARK_RATIO = 3
export const TEXT_RATIO = 4.5

/**
 * The verdict, in words and in numbers.
 *
 * `4.53:1` is the correct answer to a question most readers of this drawer are
 * not asking. The people who open Customise want to know whether the colour
 * they just picked can be seen; "4.53:1" asks them to know what a contrast
 * ratio is and where the threshold sits before it tells them anything.
 *
 * So the words are what the control shows, and the ratio travels with them in
 * `detail` for the tooltip and the accessible name. docs/CHARTS.md wants the
 * contrast reported rather than assumed, and it still is — it is just not the
 * first thing in the reader's way.
 */
export function checkContrast(colour, background, { text = false } = {}) {
  const ratio = contrast(colour, background)
  const need = text ? TEXT_RATIO : MARK_RATIO
  const pass = ratio >= need
  const words = { ratio: ratio.toFixed(2), need }
  // Four sentences, not one assembled from parts: Japanese does not put the parts in the same order.
  const said = text
    ? pass
      ? [
          t('Easy to read'),
          t(
            'Contrast {ratio}:1 against the page behind it. Clears the {need}:1 this needs to be easy to read.',
            words,
          ),
        ]
      : [
          t('Hard to read'),
          t(
            'Contrast {ratio}:1 against the page behind it. Below the {need}:1 this needs to be easy to read.',
            words,
          ),
        ]
    : pass
      ? [
          t('Easy to see'),
          t(
            'Contrast {ratio}:1 against the page behind it. Clears the {need}:1 this needs to be easy to see.',
            words,
          ),
        ]
      : [
          t('Hard to see'),
          t(
            'Contrast {ratio}:1 against the page behind it. Below the {need}:1 this needs to be easy to see.',
            words,
          ),
        ]
  return {
    ratio,
    need,
    pass,
    label: `${ratio.toFixed(2)}:1`,
    words: said[0],
    detail: said[1],
  }
}

/**
 * The six chart hues, per theme.
 *
 * One ordered palette, used by every chart in the same order — so the first
 * series is the same colour everywhere. Each entry is [strong, soft]: two solid
 * steps rather than one hue at two opacities, because a translucent bar picks
 * up the band behind it.
 *
 * Long enough for every hotel in one complexity group to get its own hue. The
 * legend says the hotel once and leans on the colour, so a wrap here makes two
 * hotels genuinely ambiguous — add a pair before adding a fifth hotel to either
 * side. No red: a red bar on a score chart reads as a verdict.
 */
export const PALETTES = {
  dark: [
    ['#4fd1c5', '#2d706e'], // teal
    ['#a3d977', '#526e45'], // lime
    ['#7aa7ff', '#4a669c'], // blue
    ['#f0a868', '#825f42'], // amber
    ['#c98bdb', '#7b598b'], // orchid
    ['#6ee7f0', '#376f77'], // cyan
  ],
  light: [
    ['#2a8c85', '#4e9f9a'], // teal
    ['#5f7d2e', '#82995f'], // lime
    ['#2f5fe0', '#6d8ee8'], // blue
    ['#9c5f18', '#b48a59'], // amber
    ['#8a3fa8', '#af80c5'], // orchid
    ['#0f7d8c', '#4c9da9'], // cyan
  ],
}

/**
 * The soft step of a pair.
 *
 * The built-in pairs are hand-picked; a colour someone chose in the drawer has
 * no partner, so one is mixed towards the page behind it. Towards the surface
 * rather than to a fixed tint, or the derived step would be invisible in one
 * theme and garish in the other.
 */
export const softStep = (strong, surface, isDark) =>
  mix(strong, surface || (isDark ? '#1c1f24' : '#ffffff'), isDark ? 0.52 : 0.62)
